import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class DropDownHotspotNodeView extends NodeViewBase implements NodeView {
  private buttonDOM: HTMLButtonElement;
  private imgDOM: HTMLImageElement;
  private onButtonClicked: (event: MouseEvent) => void;

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Create the DOM elements
    this.contentDOM = this.dom.appendChild(document.createElement('mc-lwe-dropdownhotspot-content'));
    this.buttonDOM = this.dom.appendChild(document.createElement('button'));
    this.buttonDOM.setAttribute('contenteditable', 'false');
    this.buttonDOM.setAttribute('draggable', 'false');
    this.buttonDOM.setAttribute('type', 'button');
    this.buttonDOM.classList.add('mc-lwe-dropdownhotspot-button');
    this.imgDOM = this.buttonDOM.appendChild(document.createElement('img'));
    this.imgDOM.setAttribute('draggable', 'false');
    this.imgDOM.classList.add('mc-lwe-dropdownhotspot-image');

    // Listen for button click events to fire the toggle event
    this.onButtonClicked = () => {
      // Dispatch a custom event to notify the drop down node view of the toggle
      this.dom.dispatchEvent(new CustomEvent('toggle-drop-down', {
        bubbles: true,
        composed: true,
        cancelable: false
      }));
    };

    this.buttonDOM.addEventListener('click', this.onButtonClicked);

    this.addArrowKeyNavigation(this.buttonDOM);

    // Sync the node
    this.syncNode(node);
  }

  destroy() {
    this.buttonDOM.removeEventListener('click', this.onButtonClicked);
    super.destroy();
  }

  protected getArrowKeyTargetPos(pos: number, dir: -1 | 1, element: HTMLElement): number {
    // Walk up the DOM to find the drop-down container's open state.
    // The hotspot is nested inside a dropDownHead, so parentElement is not the drop-down container.
    let isOpen = false;
    let parentEl = this.dom.parentElement;
    while (parentEl && parentEl !== this.editorView.dom) {
      if (parentEl.hasAttribute('mc-lwe-drop-down-open')) {
        isOpen = true;
        break;
      }
      parentEl = parentEl.parentElement;
    }

    const isButtonOnLeft = this.buttonDOM === this.dom.firstChild;
    // The hotspot is nested: dropDown > dropDownHead > dropDownHotspot.
    // $pos.depth is the dropDownHead level, so depth - 1 is the dropDown container level.
    const $pos = this.editorView.state.doc.resolve(pos);
    const containerDepth = $pos.depth - 1;

    if (isButtonOnLeft) {
      // Button is on the left - content is to the right
      if (dir < 0) {
        // Navigate before the drop down container
        return $pos.start(containerDepth) - 1;
      }

      // Navigate to the start of the hotspot content
      return pos + 1;
    }

    // Button is on the right - content is to the left
    if (dir < 0) {
      // Navigate to the end of the hotspot content (the text to the left of the button)
      return pos + this.node.nodeSize - 1;
    }

    // If the drop down body is open, navigate to the start of the drop down body
    if (isOpen) {
      return pos + this.node.nodeSize;
    }

    // Body is closed - navigate past the entire drop down container
    return $pos.end(containerDepth) + 1;
  }

  stopEvent(event: Event): boolean {
    return this.buttonDOM?.contains(event.target as Node);
  }
}
